/**
 * Agent Utilities - Single Source of Truth
 *
 * Pure functions for agent formatting, extraction, and validation.
 * Used across frontend and backend for consistent agent handling.
 *
 * RULES:
 * - Pure functions only (no React, no Redux, no side effects)
 * - Null-safe (handles undefined/null gracefully)
 * - Consistent fallbacks (same defaults everywhere)
 * - Fully testable (zero external dependencies)
 */

import type { Agent, ProcessedSkill, SkillOption } from "./agent.types";

type MaybeAgent = Partial<Agent> | null | undefined;

/**
 * Agent formatting and extraction utilities
 */
export const AgentUtils = {
  // ============================================================================
  // NAME FORMATTING
  // ============================================================================

  /**
   * Get full name: "John Doe"
   * Returns null if agent is null/undefined
   */
  getFullName(agent?: MaybeAgent): string | null {
    if (!agent) return null;
    return `${agent.first_name} ${agent.last_name}`.trim();
  },

  /**
   * Get display name: "John Doe" or "Unknown Agent"
   * Never returns null - always safe for display
   */
  getDisplayName(agent?: MaybeAgent): string {
    return AgentUtils.getFullName(agent) ?? "Unknown Agent";
  },

  /**
   * Get initials: "JD" or "?"
   * Useful for avatar placeholders when no profile picture
   */
  getInitials(agent?: MaybeAgent): string {
    if (!agent) return "?";
    const first = agent.first_name?.[0]?.toUpperCase() ?? "";
    const last = agent.last_name?.[0]?.toUpperCase() ?? "";
    return `${first}${last}` || "?";
  },

  /**
   * Get first name only: "John" or null
   */
  getFirstName(agent?: MaybeAgent): string | null {
    return agent?.first_name ?? null;
  },

  /**
   * Get last name only: "Doe" or null
   */
  getLastName(agent?: MaybeAgent): string | null {
    return agent?.last_name ?? null;
  },

  // ============================================================================
  // ROLE/DETAILS
  // ============================================================================

  /**
   * Get role: "Sales Assistant" or "Agent" (fallback)
   * Never returns null
   */
  getRole(agent?: MaybeAgent): string {
    return agent?.role?.trim() || "Agent";
  },

  /**
   * Get details/description
   * Returns null if empty or agent is null
   */
  getDetails(agent?: MaybeAgent): string | null {
    return agent?.details?.trim() || null;
  },

  // ============================================================================
  // PROFILE PICTURE
  // ============================================================================

  /**
   * Get profile picture URL with consistent fallback
   * Always returns a valid URL (never null)
   */
  getProfilePicture(agent?: MaybeAgent): string {
    return agent?.profile_picture ?? "/images/default-avatar.png";
  },

  /**
   * Check if agent has a custom profile picture
   */
  hasProfilePicture(agent?: MaybeAgent): boolean {
    return !!agent?.profile_picture;
  },

  // ============================================================================
  // SKILLS EXTRACTION (Agent only)
  // ============================================================================

  /**
   * Extract all skills from agent (tasks, tools, knowers, flows)
   * Returns unified ProcessedSkill array for UI display
   *
   * @param agent - Agent with full skill data
   * @returns Array of processed skills (empty if no agent)
   */
  extractSkills(agent?: MaybeAgent): ProcessedSkill[] {
    if (!agent) return [];

    return [
      // Knowers (knowledge-based tools)
      ...(agent.knowers?.filter((k) => k.isTool) || []).map(
        (knower): ProcessedSkill => ({
          id: knower.id,
          name: knower.name,
          description: knower.description,
          type: "KNOWER"
        })
      ),
      // Tasks
      ...(agent.AgentTasks || []).map(
        (task): ProcessedSkill => ({
          id: task.id,
          name: task.action,
          description: task.descriptionHL,
          type: "TASK"
        })
      ),
      // External tools
      ...(agent.ToolActionConfig || []).map(
        (tool): ProcessedSkill => ({
          id: tool.id,
          name: tool.publicName,
          description: tool.description,
          type: "EXTERNAL_TOOL"
        })
      ),
      // Flows
      ...(agent.agentFlows || []).map(
        (flow): ProcessedSkill => ({
          id: flow.id,
          name: flow.publicName,
          description: flow.description,
          type: "FLOW"
        })
      )
    ];
  },

  /**
   * Get skill options for dropdown select
   * Formatted with "/" prefix for command-style display
   *
   * @param agent - Agent with skills
   * @returns Array of {label, value} for <Select> options
   */
  getSkillOptions(agent?: MaybeAgent): SkillOption[] {
    const skills = AgentUtils.extractSkills(agent);
    return skills.map((skill) => ({
      label: `/${skill.name}`,
      value: `/${skill.name}`
    }));
  },

  /**
   * Get skills count
   * Useful for displaying "X skills available"
   */
  getSkillsCount(agent?: MaybeAgent): number {
    return AgentUtils.extractSkills(agent).length;
  },

  /**
   * Find skill by ID
   */
  findSkillById(agent: MaybeAgent, skillId: string): ProcessedSkill | null {
    const skills = AgentUtils.extractSkills(agent);
    return skills.find((s) => s.id === skillId) ?? null;
  },

  // ============================================================================
  // VALIDATION
  // ============================================================================

  /**
   * Type guard: Check if agent is fully loaded
   */
  isLoaded(agent?: MaybeAgent): agent is Agent {
    return !!agent && !!agent.id && !!agent.first_name && !!agent.last_name;
  },

  /**
   * Type guard: Check if agent has skills data
   */
  hasSkills(agent?: unknown): agent is Agent {
    return (
      !!agent &&
      typeof agent === "object" &&
      ("AgentTasks" in agent ||
        "knowers" in agent ||
        "ToolActionConfig" in agent ||
        "agentFlows" in agent)
    );
  }
};
